import type { TerminalCommand } from "./types";
import { safeJson } from "../llm/stream";

const help: TerminalCommand = {
  name: "help",
  aliases: ["?"],
  description: "List available commands.",
  run: ({ print, host }) => {
    const lines = ["Commands:"];
    // The registry is not directly reachable here; the controller injects the
    // full list by re-registering a help that closes over it. This fallback
    // lists the built-in set.
    for (const c of BUILTIN_COMMANDS) {
      lines.push(`  /${c.name}${c.usage ? " " + c.usage : ""} — ${c.description}`);
    }
    if (host.listActions().length) {
      lines.push("", "Dispatchable actions (see /actions):");
      for (const a of host.listActions()) {
        lines.push(`  ${a.name} — ${a.description}`);
      }
    }
    print(lines.join("\n"));
  },
};

const clear: TerminalCommand = {
  name: "clear",
  aliases: ["cls"],
  description: "Clear the conversation.",
  run: ({ host }) => host.clearMessages(),
};

const providers: TerminalCommand = {
  name: "providers",
  aliases: ["provider"],
  description: "List configured LLM providers.",
  run: ({ print, host }) => {
    const list = host.listProviders();
    if (!list.length) {
      print("No providers configured.");
      return;
    }
    print(
      list
        .map(
          (p) =>
            `${p.active ? "▸" : " "} ${p.id} — ${p.kind}/${p.model}${
              p.supportsTools ? " (tools)" : ""
            }`,
        )
        .join("\n"),
    );
  },
};

const use: TerminalCommand = {
  name: "use",
  aliases: ["switch"],
  usage: "<provider-id>",
  description: "Switch the active provider.",
  run: ({ argv, print, host }) => {
    const id = argv[0];
    if (!id) {
      print("Usage: /use <provider-id>. See /providers.", "system");
      return;
    }
    print(
      host.setActiveProvider(id)
        ? `Active provider is now "${id}".`
        : `No provider with id "${id}".`,
    );
  },
};

const actions: TerminalCommand = {
  name: "actions",
  description: "List Redux actions the terminal can dispatch.",
  run: ({ print, host }) => {
    const list = host.listActions();
    if (!list.length) {
      print("No Redux actions wired. Configure `redux.actions`.");
      return;
    }
    print(list.map((a) => `  ${a.name} — ${a.description}`).join("\n"));
  },
};

const dispatch: TerminalCommand = {
  name: "dispatch",
  aliases: ["d"],
  usage: "<action> [json-args]",
  description: "Dispatch a configured Redux action.",
  run: ({ args, print, host }) => {
    const firstSpace = args.search(/\s/);
    const name = firstSpace === -1 ? args : args.slice(0, firstSpace);
    const rest = firstSpace === -1 ? "" : args.slice(firstSpace + 1).trim();
    if (!name) {
      print("Usage: /dispatch <action> [json-args]. See /actions.", "system");
      return;
    }
    const arg = rest ? safeJson(rest) : undefined;
    const result = host.dispatchAction(name, arg);
    print(result.message, result.ok ? "system" : "system");
  },
};

const feed: TerminalCommand = {
  name: "feed",
  aliases: ["log"],
  description: "Show the most recent dispatched actions.",
  run: ({ print, host }) => {
    const entries = host.getActionFeed().slice(-15);
    if (!entries.length) {
      print("No actions observed yet.");
      return;
    }
    print(
      entries
        .map((e) => `  [${e.origin}] ${e.type}`)
        .join("\n"),
    );
  },
};

const stop: TerminalCommand = {
  name: "stop",
  aliases: ["abort", "cancel"],
  description: "Cancel the in-flight request.",
  run: ({ host, print }) => {
    host.abort();
    print("Cancelled.");
  },
};

/** The default command set. Consumers may extend or replace it. */
export const BUILTIN_COMMANDS: TerminalCommand[] = [
  help,
  clear,
  providers,
  use,
  actions,
  dispatch,
  feed,
  stop,
];
